import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { connect } from 'react-redux'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import AccountAccessModalContent from 'components/Account/AccountAccess/AccountAccessModalContent'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { RequestAgentCallbackConfirmationModal } from './RequestAgentCallbackConfirmationModal'
import { RequestAgentCallbackErrorModal } from './RequestAgentCallbackErrorModal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { RequestAgentCallbackWarningModal } from './RequestAgentCallbackWarningModal'
import { trackClientEvent } from 'analytics/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import RequestAgentCallbackForm from '../RequestAgentCallbackForm'

const CALLBACK_FORM_ID = 'request-agent-callback-form'

const callbackTypeToAnalyticsSubject: Record<App.GenesysAgentCallbackType, string> = {
  SALES: 'request_agent_callback_sales',
  SUPPORT: 'request_agent_callback_support',
  HOTELS_AND_VILLAS: '',
  LESA: '',
  TOURS: '',
  FLIGHTS: '',
}

export interface RequestAgentCallbackResult {
  firstName?: string
  lastName?: string
  type: App.GenesysAgentCallbackType
  email?: string
  phone: { phone: string, prefix: string }
  destination?: string
  travelDates?: string
  message: string
  numberOfTravellers?: string
  callbackDate: Date
  callbackTime: string
  timezone: string
}

export interface MappedRequestAgentCallbackResult {
  chatId: string | undefined
  customerId: string
  customerPhone: string
  type: App.GenesysAgentCallbackType
  isImmediateCallback: boolean
  updatePhoneNumber: boolean
  customerHasRecentlyScheduledCallback: boolean
  customer: {
    firstName: string
    lastName: string
    email: string
    phone: string
    message?: string
    destination?: string
    numberOfTravellers?: string
    travelDates?: string
    countryCode?: string
    callbackDate?: Date
    callbackTime?: string
    timezone?: string
  }
}

interface MappedStateProps {
  loggedIn: boolean
  account: App.AuthAccount
}

interface Props {
  type: App.GenesysAgentCallbackType
  chatId?: string
  onRequestSubmitted?: (results: MappedRequestAgentCallbackResult) => void | Promise<void>
  messagePlaceholder?: string
  isLESATouchpoint?: boolean
  filters?: App.OfferListFilters
  location?: string
  prefillFirstName?: string
  prefillLastName?: string
  prefillEmail?: string
  prefillPhone?: string
  prefillPhonePrefix?: string
  dismissable?: boolean
  initialMode?: App.UiAccountModalMode
  resetPasswordToken?: string
  hotelOfferDetails?: App.AccountModalOfferDetails
  defaultEmail?: string
  callbackPath?: string
  onStartAnotherRequest?: () => void
}

function RequestAgentCallbackModal(props: MappedStateProps & Props) {
  const {
    initialMode = 'requestAgentCallbackLogin',
    resetPasswordToken,
    hotelOfferDetails,
    dismissable,
    defaultEmail,
    callbackPath,
    onStartAnotherRequest,
    loggedIn,
    account,
    type,
    chatId,
    onRequestSubmitted,
    messagePlaceholder,
    isLESATouchpoint,
    filters,
    location,
    prefillFirstName,
    prefillLastName,
    prefillEmail,
    prefillPhone,
    prefillPhonePrefix,
  } = props

  const intl = useIntl()

  useEffect(() => {
    trackClientEvent({
      action: 'request_agent_callback_form_displayed',
      subject: callbackTypeToAnalyticsSubject[type],
      category: AnalyticsPage.requestAgentCallback,
      type: 'interaction',
    })
  }, [type])

  const [mode, setMode] = useState<App.UiAccountModalMode>(initialMode)
  const loading = useAppSelector(state => state.auth.processing)
  const modalElement = useModalElementContext<MappedRequestAgentCallbackResult | undefined>()
  const [results, setResults] = useState<MappedRequestAgentCallbackResult | null>(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const showCallbackForm = !showConfirmationModal && !showWarningModal && !showErrorModal

  const handleSubmitted = useCallback(async(results: MappedRequestAgentCallbackResult) => {
    setResults(results)
    try {
      await onRequestSubmitted?.(results)
    } catch {
      // Don't block the callback request success flow on chat-message follow-up failures.
    }
    if (isLESATouchpoint) {
      modalElement.resolve(results)
    } else {
      setShowConfirmationModal(true)
    }
  }, [isLESATouchpoint, modalElement, onRequestSubmitted])

  const handleDuplicateCallback = useCallback((results: MappedRequestAgentCallbackResult) => {
    if (isLESATouchpoint) {
      modalElement.resolve(results)
    } else {
      setResults(results)
      setShowWarningModal(true)
    }
  }, [isLESATouchpoint, modalElement])

  const handleError = useCallback(() => {
    setShowErrorModal(true)
  }, [])

  return (
    <ModalBase>
      <LoadingIndicator visible={loading} floating />
      {!loggedIn &&
        <AccountAccessModalContent
          mode={mode}
          defaultEmail={defaultEmail}
          onModeChange={setMode}
          resetPasswordToken={resetPasswordToken}
          hotelOfferDetails={hotelOfferDetails}
          callbackPath={callbackPath}
          dismissable={dismissable}
        />}
      {loggedIn && showCallbackForm &&
        <>
          <ModalHeader title={intl.formatMessage({
            id: 'MdjLVExO_9sMGVOXIif4',
            defaultMessage: 'Request a callback',
            description: 'Title for the request agent callback modal in the agent portal',
          })}
          />
          <ModalBody>
            <ModalContent>
              <RequestAgentCallbackForm
                formId={CALLBACK_FORM_ID}
                type={type}
                requester={loggedIn ? { type: 'authenticated', account } : { type: 'guest' }}
                chatId={chatId}
                messagePlaceholder={messagePlaceholder}
                filters={filters}
                location={location}
                prefillFirstName={prefillFirstName}
                prefillLastName={prefillLastName}
                prefillEmail={prefillEmail}
                prefillPhone={prefillPhone}
                prefillPhonePrefix={prefillPhonePrefix}
                onSubmittingChange={setIsSubmitting}
                onSubmitted={handleSubmitted}
                onDuplicateCallback={handleDuplicateCallback}
                onError={handleError}
              />
            </ModalContent>
          </ModalBody>
          {isSubmitting && <LoadingIndicator floating />}
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: '4PlRTYD7woo__6dNbhtu',
                defaultMessage: 'Submit Request',
                description: 'Submit button text for callback request form in a modal dialog',
              }),
              type: 'submit',
              form: CALLBACK_FORM_ID,
              disabled: isSubmitting,
            }}
          />
        </>}
      {showConfirmationModal && results && (
        <RequestAgentCallbackConfirmationModal
          customer={results.customer}
          onStartAnotherRequest={onStartAnotherRequest}
        />
      )}
      {showWarningModal && <RequestAgentCallbackWarningModal />}
      {showErrorModal && <RequestAgentCallbackErrorModal />}
    </ModalBase>
  )
}

export default connect<MappedStateProps, undefined, Partial<Props>, App.State>(
  (state) => ({
    loggedIn: isLoggedInSelector(state),
    account: state.auth.account,
  }),
)(RequestAgentCallbackModal)
